// Imports
// React
import React, { useState } from 'react';
// Third-party libraries
import { z } from 'zod';
// Utils
import { createLogger } from '../../../utils/logger';

const logger = createLogger('EmailCaptureForm');

// Zod validation schema
const emailSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .max(254, 'Email is too long'),
});

interface EmailCaptureFormProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSubmit'> {
  onSubmit: (email: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

// Component Definition
export const EmailCaptureForm: React.FC<EmailCaptureFormProps> = ({
  onSubmit,
  isLoading = false,
  error = null,
  className = '',
  ...props
}) => {
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    // Clear validation error when user types
    setValidationError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate with Zod
    try {
      const validatedData = emailSchema.parse({ email: email.trim() });
      logger.info('Email validation successful');

      // Clear any previous errors
      setValidationError(null);

      // Call parent submit handler
      await onSubmit(validatedData.email);
    } catch (err) {
      if (err instanceof z.ZodError) {
        const firstError = err.errors[0]?.message;
        setValidationError(firstError || 'Invalid email');
        logger.warn('Email validation failed', { error: firstError });
      } else {
        logger.error('Unexpected error during form submission', { error: err });
        setValidationError('An unexpected error occurred');
      }
    }
  };

  const getInputStatus = () => {
    if (isFocused) return 'focus';
    if (validationError || error) return 'error';
    if (email && !validationError) return 'success';
    return 'default';
  };

  const inputStatus = getInputStatus();

  const inputClasses = {
    default: 'border-gray-300 dark:border-gray-600 focus:border-pink-500 focus:ring-pink-500',
    focus: 'border-pink-500 ring-2 ring-pink-500/20',
    success: 'border-green-500 dark:border-green-400 focus:border-green-500 focus:ring-green-500',
    error: 'border-red-500 dark:border-red-400 focus:border-red-500 focus:ring-red-500',
  };

  return (
    <div className={className} {...props}>
      <form onSubmit={handleSubmit} className="w-full">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex-1">
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={handleEmailChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              disabled={isLoading}
              placeholder="Join the waitlist"
              className={`w-full rounded-xl border bg-white px-6 py-4 text-lg font-medium text-gray-900 antialiased transition-all duration-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-neutral-800 dark:text-white dark:placeholder:text-gray-500 ${inputClasses[inputStatus]}`}
              aria-label="Email address"
              aria-invalid={!!(validationError || error)}
              aria-describedby={validationError || error ? 'email-error' : undefined}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !email}
            className="rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 px-8 py-4 text-lg font-bold text-white antialiased shadow-lg transition-all duration-200 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-neutral-900"
          >
            {isLoading ? 'Submitting...' : 'Get Early Access'}
          </button>
        </div>

        {/* Error Messages */}
        {(validationError || error) && (
          <div
            id="email-error"
            className="mt-3 text-sm font-medium text-red-600 dark:text-red-400"
            role="alert"
          >
            {validationError || error}
          </div>
        )}
      </form>
    </div>
  );
};
